import React, { useRef, useState } from "react";
import { getImageUrl } from "@/utils/imageUrl";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import { Briefcase, Camera, UploadCloud } from "lucide-react";
import { toast } from "react-toastify";
import { uploadSponsorLogo, removeSponsorLogo } from "@/store/slices/sponsorSlice";
import Button from "@/components/common/Button";
import ImageCropper from "@/components/common/ImageCropper";

const SettingsBranding = () => {
  const dispatch = useDispatch();
  const { sponsor, isLoading } = useSelector((state) => state.sponsor);
  const fileInputRef = useRef(null);

  const [imageToCrop, setImageToCrop] = useState(null);

  const handleLogoClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      return;
    }

    setImageToCrop(URL.createObjectURL(file));
    e.target.value = null; // reset input
  };

  const handleCropDone = async (croppedBlob) => {
    const file = new File([croppedBlob], `logo_cropped.jpeg`, {
      type: "image/jpeg",
    });

    const formData = new FormData();
    formData.append("logo", file);

    try {
      await dispatch(uploadSponsorLogo(formData)).unwrap();
      toast.success("Logo uploaded successfully");
      setImageToCrop(null);
    } catch (error) {
      toast.error(error?.message || "Failed to upload logo");
    }
  };

  const handleRemoveLogo = async () => {
    try {
      await dispatch(removeSponsorLogo()).unwrap();
      toast.success("Logo removed successfully");
    } catch (error) {
      toast.error(error?.message || "Failed to remove logo");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-2xl"
    >
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-8 text-center">
        <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-2">
          Company Logo
        </h2>
        <p className="text-gray-500 dark:text-gray-400 mb-8 max-w-sm mx-auto">
           This logo will be displayed on your profile and sponsorship details. 
           Recommended 500x500px.
        </p>

        <div className="flex flex-col items-center">
          <div
            className="relative group cursor-pointer w-40 h-40 rounded-3xl bg-gray-50 dark:bg-gray-900 border-2 border-dashed border-gray-300 dark:border-gray-700 flex items-center justify-center overflow-hidden hover:border-teal-500 transition-colors"
            onClick={handleLogoClick}
          >
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept="image/*"
              onChange={handleFileChange}
            />

            {sponsor?.logo_url ? (
              <img
                src={getImageUrl(sponsor.logo_url)}
                alt={sponsor.company_name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="flex flex-col items-center text-gray-400">
                 <Briefcase className="w-12 h-12 mb-2" />
                 <span className="text-xs">No Logo</span>
              </div>
            )}

            {/* Hover visual */}
            <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-white">
              <Camera className="w-8 h-8 mb-2" />
              <span className="text-sm font-medium">Change Photo</span>
            </div>
          </div>

          <div className="mt-8 flex gap-3">
             <Button variant="outline" onClick={handleLogoClick}>
                <UploadCloud className="w-4 h-4 mr-2" />
                Upload New Logo
             </Button>
             {sponsor?.logo_url && (
                <Button 
                    variant="danger" 
                    size="sm" 
                    className="bg-red-50 text-red-600 hover:bg-red-100 border-none dark:bg-red-900/20 dark:hover:bg-red-900/40"
                    onClick={handleRemoveLogo}
                    loading={isLoading}
                >
                    Remove
                </Button>
             )}
          </div>
        </div>
      </div>

      {/* CROPPER MODAL */}
      {imageToCrop && (
        <ImageCropper
          imageSrc={imageToCrop}
          aspectRatio={1}
          onCropDone={handleCropDone}
          onCancel={() => setImageToCrop(null)}
        />
      )}
    </motion.div>
  );
};

export default SettingsBranding;
